import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { athleteSetPassword } from "../../lib/api";
import { useAthleteAuth } from "../../lib/athleteAuth";
import { toast } from "sonner";

export default function AthleteSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAthlete } = useAthleteAuth();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const res = await athleteSetPassword(token, password);
      setAthlete(res.athlete);
      toast.success("Password set — you're in.");
      navigate("/athlete/home", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't set password.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--tir-ivory)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="font-serif-display text-3xl mb-3">Missing invite token</div>
          <p className="text-sm text-[var(--tir-muted)] mb-5">
            This link is incomplete. Use the full link your coach sent you, or ask them to resend.
          </p>
          <Link to="/" className="text-xs tracking-[0.22em] uppercase text-[var(--tir-burgundy)]">
            ← Back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--tir-ivory)] flex flex-col" data-testid="athlete-setup-page">
      <div className="max-w-md w-full mx-auto px-6 py-10 flex-1 flex flex-col justify-center">
        <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[var(--tir-navy)] mb-2">
          Athlete Portal
        </div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[var(--tir-ink)] mb-1">
          Set your password
        </h1>
        <p className="text-sm text-[var(--tir-muted)] mb-8">
          Welcome — choose a password to access your training portal.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              New password (6+ chars)
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="athlete-setup-password"
              autoFocus
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2.5 text-base focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Confirm password
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="athlete-setup-confirm"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2.5 text-base focus:outline-none"
            />
          </div>
          {error && <div className="text-xs text-[var(--tir-burgundy)] py-1">{error}</div>}
          <button
            type="submit"
            disabled={busy}
            data-testid="athlete-setup-submit"
            className="w-full py-3 bg-[var(--tir-navy)] hover:bg-[var(--tir-navy)]/90 text-white text-xs tracking-[0.24em] uppercase font-semibold disabled:opacity-50"
          >
            {busy ? "Setting up…" : "Set password & sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
